// ============================================================
// FOLDER CONVENTIONS — Customer-Zero project folder layout (FE-CP-2)
// ------------------------------------------------------------
// Single source of truth for the "01. RFQ Package" / "02. Site
// Documentation Package" / "03. Quote" / ... numbered subfolder
// convention RJ BIRD's team uses inside each project folder.
//
// Customer-Zero (Rodney) operates on a sync root like
//   C:\<sync-root>\[1. Quote Requests]\
// with project folders of the shape
//   [YYYY.MM.DD PR][SP####-QR######] - Suburb - Street - Description\
// and inside each project, ten numbered subfolders:
//   01. RFQ Package          ← intake source
//   02. Site Documentation Package  ← intake source
//   03. Quote                ← QM output sink (PDF lands here)
//   04. Work Order
//   05. Contract
//   06. Build SoW
//   07. Compliance
//   08. Invoices
//   09. Final Inspections
//   10. Defects
//
// The matchers are tolerant of minor operator variation (missing dot,
// extra/missing space, case-insensitive) but anchored on the leading
// 0N prefix + the role name. They explicitly do NOT match
// "Archived RFQs", "Old Quotes", or similar wider-context folders —
// the leading 0N anchor + word boundary is the discriminator.
// ============================================================

export type CzSubfolderRole =
    | 'rfq'
    | 'site-docs'
    | 'quote-out'
    | 'work-order'
    | 'contract'
    | 'build-sow'
    | 'compliance'
    | 'invoices'
    | 'final-inspections'
    | 'defects';

export type CzSubfolderKind = 'intake' | 'output' | 'other';

export interface CzSubfolderSpec {
    role: CzSubfolderRole;
    number: number;
    canonicalName: string;
    match: RegExp;
    kind: CzSubfolderKind;
}

export interface QuoteFilenameParts {
    suburb?: string;
    street?: string;
    mmyy?: string;
    description?: string;
}

export interface ResolveQuoteOutputFolderOptions {
    create?: boolean;
}

export const CZ_PROJECT_SUBFOLDERS: readonly CzSubfolderSpec[] = Object.freeze([
    // Intake matching is tolerant about the operator's separators and leading
    // zero, but exact about the complete folder name. The end anchor matters:
    // `01. RFQ Package - old` and `02. Site Documentation Package copy` are
    // lookalikes, not the two live packages that may be queued together.
    { role: 'rfq', number: 1, canonicalName: '01. RFQ Package', match: /^0?1[.\s_-]+rfq(?:[\s_-]+pack(?:age)?)?$/i, kind: 'intake' },
    { role: 'site-docs', number: 2, canonicalName: '02. Site Documentation Package', match: /^0?2[.\s_-]+(?:site[\s_-]+documentation[\s_-]+pack(?:age)?|sd[\s_-]*pack(?:age)?)$/i, kind: 'intake' },
    { role: 'quote-out', number: 3, canonicalName: '03. Quote', match: /^0?3[.\s_-]+quotes?\b/i, kind: 'output' },
    { role: 'work-order', number: 4, canonicalName: '04. Work Order', match: /^0?4[.\s_-]+work[\s_-]+order\b/i, kind: 'other' },
    { role: 'contract', number: 5, canonicalName: '05. Contract', match: /^0?5[.\s_-]+contract\b/i, kind: 'other' },
    { role: 'build-sow', number: 6, canonicalName: '06. Build SoW', match: /^0?6[.\s_-]+build[\s_-]+sow\b/i, kind: 'other' },
    { role: 'compliance', number: 7, canonicalName: '07. Compliance', match: /^0?7[.\s_-]+compliance\b/i, kind: 'other' },
    { role: 'invoices', number: 8, canonicalName: '08. Invoices', match: /^0?8[.\s_-]+invoices?\b/i, kind: 'other' },
    { role: 'final-inspections', number: 9, canonicalName: '09. Final Inspections', match: /^0?9[.\s_-]+final[\s_-]+inspections?\b/i, kind: 'other' },
    { role: 'defects', number: 10, canonicalName: '10. Defects', match: /^10[.\s_-]+defects?\b/i, kind: 'other' },
]);

/** Subfolder roles whose files should be imported into the new project. */
export const INTAKE_ROLES: readonly CzSubfolderRole[] = Object.freeze(['rfq', 'site-docs']);

/** Subfolder role where the generated quote PDF lands. */
export const OUTPUT_ROLE: CzSubfolderRole = 'quote-out';

/**
 * Dedicated subfolder for QuoteMaker's GENERATED WORKING FILES (synced photos,
 * imported reference docs, the QuoteData.json backup) — kept out of RJ Bird's
 * curated 01..10 template so quote generation never dirties the project folder
 * root (Peter 2026-06-02). The numeric `99.` prefix sorts it after 10. Defects
 * and matches the team's "NN. Name" convention. NOTE: the final quote PDF still
 * lands in "03. Quote" (OUTPUT_ROLE) via resolveQuoteOutputFolder — this folder
 * is for QM's working artefacts, not the curated deliverable.
 */
export const QM_OUTPUT_SUBFOLDER: string = '99. QM';

/**
 * Classify a directory entry name as a Customer-Zero subfolder.
 * @param {string} dirName
 * @returns {CzSubfolderSpec | null}
 */
export function matchProjectSubfolder(dirName?: any): CzSubfolderSpec | null {
    if (!dirName || typeof dirName !== 'string') return null;
    const trimmed = dirName.trim();
    if (!trimmed) return null;
    for (const spec of CZ_PROJECT_SUBFOLDERS) {
        if (spec.match.test(trimmed)) return spec;
    }
    return null;
}

/**
 * Detect whether a directory handle is a Customer-Zero project folder by
 * looking for at least one of the intake subfolders (`01. RFQ Package` or
 * `02. Site Documentation Package`). Async because we must enumerate the
 * children. Stops early on first match.
 *
 * @param {FileSystemDirectoryHandle} dirHandle
 * @returns {Promise<boolean>}
 */
export async function isCzProjectFolder(dirHandle?: any): Promise<boolean> {
    if (!dirHandle || typeof dirHandle.values !== 'function') return false;
    try {
        for await (const entry of dirHandle.values()) {
            if (entry.kind !== 'directory') continue;
            const spec = matchProjectSubfolder(entry.name);
            if (spec && spec.kind === 'intake') return true;
        }
    } catch {
        return false;
    }
    return false;
}

/**
 * Enumerate the CZ subfolders present in a project directory, returning a
 * map of role → directory handle. Subfolders that don't match any CZ spec
 * are ignored (callers that need a full listing should use `dirHandle.values()`
 * directly).
 *
 * @param {FileSystemDirectoryHandle} dirHandle
 * @returns {Promise<Record<CzSubfolderRole, FileSystemDirectoryHandle | null>>}
 */
export async function enumerateCzSubfolders(dirHandle?: any): Promise<Record<CzSubfolderRole, any | null>> {
    const result: any = Object.fromEntries(CZ_PROJECT_SUBFOLDERS.map(s => [s.role, null]));
    if (!dirHandle || typeof dirHandle.values !== 'function') return result;
    try {
        for await (const entry of dirHandle.values()) {
            if (entry.kind !== 'directory') continue;
            const spec = matchProjectSubfolder(entry.name);
            if (spec && !result[spec.role]) result[spec.role] = entry;
        }
    } catch {
        // Permission denied or transient error — return what we have.
    }
    return result;
}

/**
 * Resolve (or create) the `03. Quote` output subfolder under a project
 * directory. If a tolerant match exists (e.g. `03 Quote`, `03.Quote`), we
 * use that handle. Otherwise we create the canonical `03. Quote`.
 *
 * @param {FileSystemDirectoryHandle} projectDirHandle
 * @param {object} [options]
 * @param {boolean} [options.create=true]
 * @returns {Promise<FileSystemDirectoryHandle | null>}
 */
export async function resolveQuoteOutputFolder(projectDirHandle?: any, { create = true }: ResolveQuoteOutputFolderOptions = {}): Promise<any | null> {
    if (!projectDirHandle) return null;
    const subs = await enumerateCzSubfolders(projectDirHandle);
    if (subs['quote-out']) return subs['quote-out'];
    if (!create) return null;
    const spec = CZ_PROJECT_SUBFOLDERS.find((s: CzSubfolderSpec) => s.role === 'quote-out');
    try {
        return await projectDirHandle.getDirectoryHandle(spec!.canonicalName, { create: true });
    } catch {
        return null;
    }
}

// ── Filename conventions ────────────────────────────────────────────

const FILENAME_INVALID = /[\\/:*?"<>|]/g; // Windows-reserved characters
const FILENAME_COLLAPSE_DASHES = /\s*-\s*-\s*/g;
const FILENAME_TRIM_DASH = /^\s*-\s*|\s*-\s*$/g;

function sanitiseFilenameSegment(value?: any): string {
    return String(value ?? '')
        .replace(FILENAME_INVALID, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Build the Customer-Zero quote filename. Pattern (verbatim from Peter
 * 2026-05-12): `Mosman - Hampden St - (0526) - Unit 9 & 11`. Components:
 *
 *   <suburb> - <street> - (<mmyy>) - <description>
 *
 * Missing components are dropped (with their adjacent ` - `). The
 * result has no extension — caller appends `.pdf` etc.
 *
 * @param {object} parts
 * @param {string} [parts.suburb]
 * @param {string} [parts.street]
 * @param {string} [parts.mmyy] - MMYY string e.g. "0526" for May 2026
 * @param {string} [parts.description] - e.g. "Unit 9 & 11" or "CP Structural Remedial Works"
 * @returns {string}
 */
export function buildQuoteFilename({ suburb, street, mmyy, description }: QuoteFilenameParts = {}): string {
    const parts = [
        sanitiseFilenameSegment(suburb),
        sanitiseFilenameSegment(street),
        mmyy ? `(${sanitiseFilenameSegment(mmyy)})` : '',
        sanitiseFilenameSegment(description),
    ].filter(Boolean);
    const joined = parts.join(' - ')
        .replace(FILENAME_COLLAPSE_DASHES, ' - ')
        .replace(FILENAME_TRIM_DASH, '')
        .trim();
    return joined || 'Quote';
}

/**
 * Given a base filename + extension, return the next non-conflicting name
 * in the given directory. Follows Windows Explorer's `(1)` / `(2)` style.
 * Pure-async over `directoryHandle.getFileHandle(name)`; never throws.
 *
 * @param {FileSystemDirectoryHandle} directoryHandle
 * @param {string} baseName - filename without extension, e.g. "Mosman - Hampden St - (0526)"
 * @param {string} extension - extension WITH leading dot, e.g. ".pdf"
 * @returns {Promise<string>} the candidate filename to use
 */
export async function resolveFilenameWithConflict(directoryHandle: any, baseName?: any, extension: string = ''): Promise<string> {
    const safeBase = sanitiseFilenameSegment(baseName) || 'Untitled';
    const safeExt = String(extension || '').startsWith('.') ? extension : (extension ? `.${extension}` : '');
    // Try canonical first.
    const exists = async (name: string): Promise<boolean> => {
        try {
            await directoryHandle.getFileHandle(name);
            return true;
        } catch {
            return false;
        }
    };
    const canonical = `${safeBase}${safeExt}`;
    if (!(await exists(canonical))) return canonical;
    // Already taken — sweep (1) through (999); cap protects against pathological state.
    for (let i = 1; i < 1000; i++) {
        const candidate = `${safeBase} (${i})${safeExt}`;
        if (!(await exists(candidate))) return candidate;
    }
    // Pathological fallback — date-stamp.
    const stamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
    return `${safeBase} ${stamp}${safeExt}`;
}
